import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { getAuthToken, clearAuthToken } from '@/utils/auth';
import { getCurrentUser, logout as logoutService } from '@/services/authService';
import { lockVault, getVaultConfig, initializeVault } from '@/services/vaultService';

const AuthContext = createContext(null);

// Set up axios interceptor to attach Bearer token to all requests
axios.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.withCredentials = true;
  return config;
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const authCheckDone = useRef(false);

  const checkAuth = useCallback(async () => {
    const isOAuthReturn = window.location.hash?.includes('session_id=')
      || window.location.href?.includes('session_id=');
    if (isOAuthReturn) {
      setLoading(false);
      return;
    }

    const token = getAuthToken();
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      const userData = await getCurrentUser();
      setUser(userData);

      // Restore vault tier on page refresh
      try {
        const vaultConfig = await getVaultConfig();
        if (vaultConfig.exists && vaultConfig.security_tier === 'vault') {
          await initializeVault(null, null, 'vault', null);
        }
      } catch (e) {
        // Non-blocking — vault state will be 'cloud' by default
      }
    } catch {
      setUser(null);
      clearAuthToken();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authCheckDone.current) return;
    authCheckDone.current = true;
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try {
      lockVault(); // Wipe encryption keys from memory
      await logoutService();
      setUser(null);
      clearAuthToken();
    } catch (error) {
      console.error('Logout failed:', error);
      lockVault();
      setUser(null);
      clearAuthToken();
    }
  };

  const value = {
    user,
    loading,
    setUser,
    logout,
    refreshUser: checkAuth
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};